import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a || 1;
}

function simplifyFrac(num, den) {
  if (den === 0) return [num, den];
  let n = num;
  let d = den;
  if (d < 0) {
    n = -n;
    d = -d;
  }
  const g = gcd(n, d);
  return [n / g, d / g];
}

function fracToMixedLatex(num, den) {
  const [n, d] = simplifyFrac(num, den);
  if (d === 1) return `${n}`;
  const sign = n < 0 ? "-" : "";
  const absN = Math.abs(n);
  const whole = Math.floor(absN / d);
  const rem = absN % d;
  if (whole === 0) return `${sign}\\dfrac{${absN}}{${d}}`;
  if (rem === 0) return `${sign}${whole}`;
  return `${sign}${whole}\\dfrac{${rem}}{${d}}`;
}

function parseVal(v) {
  if (v && typeof v === "object" && Number.isFinite(v.parsed)) return v.parsed;
  return NaN;
}

function term(coeff, symbol, isFirst) {
  const abs = Math.abs(coeff);
  const core = `${abs}${symbol}`;
  if (isFirst) return coeff < 0 ? `-${core}` : core;
  return coeff < 0 ? ` - ${core}` : ` + ${core}`;
}

function buildEquation(a, b, c) {
  return `${term(a, "x", true)}${term(b, "y", false)} = ${c}`;
}

function pickCoeff() {
  let v = 0;
  while (Math.abs(v) < 2) v = randInt(-6, 6);
  return v;
}

export function generateTask() {
  let a1 = 2;
  let b1 = 2;
  let c1 = 0;
  let a2 = 2;
  let b2 = 2;
  let c2 = 0;
  let x = 0;
  let y = 0;
  let det = 0;

  while (true) {
    a1 = pickCoeff();
    b1 = pickCoeff();
    a2 = pickCoeff();
    b2 = pickCoeff();
    det = a1 * b2 - a2 * b1;
    if (det === 0) continue;

    x = randInt(-8, 8);
    y = randInt(-8, 8);

    c1 = a1 * x + b1 * y;
    c2 = a2 * x + b2 * y;

    if (Math.abs(c1) > 90 || Math.abs(c2) > 90) continue;
    break;
  }

  return {
    type: "LinearSystemSubstitutionNoUnitCoeffTask",
    data: {
      a1,
      b1,
      c1,
      a2,
      b2,
      c2,
      xNum: x,
      xDen: 1,
      yNum: y,
      yDen: 1,
      systemLatex: `\\begin{cases} ${buildEquation(a1, b1, c1)} \\\\ ${buildEquation(
        a2,
        b2,
        c2
      )} \\end{cases}`,
    },
  };
}

export function renderTask({ data }) {
  const prompt = data?.neutralPrompt
    ? "Rozwi\u0105\u017c uk\u0142ad r\u00f3wna\u0144:"
    : "Rozwi\u0105\u017c uk\u0142ad r\u00f3wna\u0144 metod\u0105 podstawiania:";
  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>{prompt}</p>
      <BlockMath math={data.systemLatex} />
    </div>
  );
}

export function renderInput(_task, userAnswer = {}, onChange, disabled) {
  const xVal = userAnswer?.x || "";
  const yVal = userAnswer?.y || "";
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <InlineMath math={"x ="} />
        <FractionInput
          value={xVal}
          onChange={(v) => onChange({ ...userAnswer, x: v })}
          disabled={disabled}
          label="x"
          placeholder="np. 3/2 lub 1.5"
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math={"y ="} />
        <FractionInput
          value={yVal}
          onChange={(v) => onChange({ ...userAnswer, y: v })}
          disabled={disabled}
          label="y"
          placeholder="np. -2/3 lub -0.67"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const ux = parseVal(userAnswer?.x);
  const uy = parseVal(userAnswer?.y);
  if (!Number.isFinite(ux) || !Number.isFinite(uy)) return { correct: false };
  const ex = data.xNum / data.xDen;
  const ey = data.yNum / data.yDen;
  return { correct: Math.abs(ux - ex) < 0.01 && Math.abs(uy - ey) < 0.01 };
}

export function renderAnswer({ data }) {
  return (
    <div
      className="flex flex-col"
      style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.25 }}
    >
      <div style={{ marginBottom: "0.12rem" }}>
        <InlineMath math={`x = ${fracToMixedLatex(data.xNum, data.xDen)}`} />
      </div>
      <div>
        <InlineMath math={`y = ${fracToMixedLatex(data.yNum, data.yDen)}`} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const xRaw = userAnswer?.x?.raw || "";
  const yRaw = userAnswer?.y?.raw || "";
  if (!xRaw && !yRaw) return <i>(brak odpowiedzi)</i>;

  return (
    <div
      className="flex flex-col"
      style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.25 }}
    >
      {xRaw ? (
        <div style={{ marginBottom: "0.12rem" }}>
          <InlineMath math={formatLatexPreview(xRaw, "x")} />
        </div>
      ) : (
        <i>(brak x)</i>
      )}
      {yRaw ? (
        <div>
          <InlineMath math={formatLatexPreview(yRaw, "y")} />
        </div>
      ) : (
        <i>(brak y)</i>
      )}
    </div>
  );
}

export function renderExplanation({ data }) {
  const { a1, b1, c1, a2, b2, c2, xNum, xDen, yNum, yDen } = data;
  const xLatex = fracToMixedLatex(xNum, xDen);
  const yLatex = fracToMixedLatex(yNum, yDen);

  const yCoeffAfterSub = -a2 * b1 + a1 * b2;
  const rhsAfterSub = c2 * a1 - a2 * c1;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1:</strong> Z pierwszego równania wyznaczamy <InlineMath math="x" />:
      </p>
      <BlockMath math={`${a1}x + ${b1}y = ${c1}`} />
      <BlockMath math={`${a1}x = ${c1} ${b1 >= 0 ? "-" : "+"} ${Math.abs(b1)}y`} />
      <BlockMath math={`x = \\dfrac{${c1} ${b1 >= 0 ? "-" : "+"} ${Math.abs(b1)}y}{${a1}}`} />

      <p>
        <strong>Krok 2:</strong> Podstawiamy do drugiego równania:
      </p>
      <BlockMath
        math={`${a2}\\left(\\dfrac{${c1} ${b1 >= 0 ? "-" : "+"} ${Math.abs(b1)}y}{${a1}}\\right) + ${b2}y = ${c2}`}
      />
      <BlockMath math={`${a2}\\left(${c1} ${b1 >= 0 ? "-" : "+"} ${Math.abs(b1)}y\\right) + ${a1 * b2}y = ${c2 * a1}`} />
      <BlockMath math={`${yCoeffAfterSub}y = ${rhsAfterSub}`} />
      <BlockMath math={`y = ${yLatex}`} />

      <p>
        <strong>Krok 3:</strong> Liczymy <InlineMath math="x" />:
      </p>
      <BlockMath math={`x = ${xLatex}`} />
      <BlockMath math={`x = ${xLatex},\\quad y = ${yLatex}`} />

      <p>
        <strong>Odpowiedź:</strong>{" "}
        <InlineMath math={`\\left(x,y\\right)=\\left(${xLatex}, ${yLatex}\\right)`} />
      </p>
    </div>
  );
}




